/**
 * Shared form field building blocks for the D3 core resources. Built on the
 * canonical shadcn `Form` primitives (which wrap react-hook-form). The Refine
 * `useForm` adapter returns a react-hook-form instance, so these components
 * accept its `control` directly.
 *
 * Co-located under `projects/` (Frontend D3) and imported by the sibling
 * resource folders.
 */
import * as React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";

/** Turn an enum value (`in_progress`) into a label (`In Progress`). */
export function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Single-line text field. */
export function TextField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  type = "text",
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string;
  description?: string;
  type?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholder}
              {...field}
              value={field.value ?? ""}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Multi-line text / markdown field. */
export function TextAreaField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  rows = 4,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string;
  description?: string;
  rows?: number;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea
              rows={rows}
              placeholder={placeholder}
              {...field}
              value={field.value ?? ""}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Dropdown bound to a readonly tuple of enum values. */
export function SelectField<TValues extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholder = "Select…",
  description,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  options: readonly string[];
  placeholder?: string;
  description?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            onValueChange={field.onChange}
            value={(field.value as string | undefined) ?? undefined}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {humanize(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Markdown field — controlled `MarkdownEditor` (textarea + preview) wired into
 * the form. Used for agent-readable summaries/bodies (frontend.spec §18).
 */
export function MarkdownField<TValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  placeholder?: string;
  description?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <MarkdownEditor
              value={(field.value as string | null | undefined) ?? ""}
              onChange={field.onChange}
              placeholder={placeholder}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Two-column responsive grid for form fields. */
export function FormGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  );
}
